<template>
  <div class="card extension-statistics">
    <div class="card-body">
      <div class="chart-header">
        <h6 class="chart-title">Chamadas por usuário</h6>
        <div class="chart-controls">
          <div class="chart-drill" title="Nível de análise">
            <b-dropdown
              variant="link"
              size="sm"
              no-caret
              toggle-class="chart-drill-toggle"
              menu-class="chart-drill-menu"
              :text="granularityLabel(granularity)">
              <b-dropdown-item
                v-for="lvl in levels"
                :key="lvl"
                :active="lvl === granularity"
                @click="$emit('set-granularity', lvl)">{{ granularityLabel(lvl) }}</b-dropdown-item>
            </b-dropdown>
            <div class="chart-drill-buttons">
              <button type="button" :disabled="!canUp" @click="step('up')" title="Ampliar período">
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="18 15 12 9 6 15" />
                </svg>
              </button>
              <button type="button" :disabled="!canDown" @click="step('down')" title="Detalhar período">
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>
            </div>
          </div>
          <div class="chart-type-toggle">
            <button type="button" :class="{ active: chartType === 'line' }" @click="setChartType('line')" title="Linha">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
              </svg>
            </button>
            <button type="button" :class="{ active: chartType === 'bar' }" @click="setChartType('bar')" title="Colunas">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <line x1="18" y1="20" x2="18" y2="10" />
                <line x1="12" y1="20" x2="12" y2="4" />
                <line x1="6" y1="20" x2="6" y2="14" />
              </svg>
            </button>
          </div>
        </div>
      </div>
      <div class="chart-canvas chart-canvas--user">
        <canvas ref="canvas" />
      </div>
    </div>
  </div>
</template>

<script>
import Chart from 'chart.js'
import {
  BDropdown,
  BDropdownItem
} from 'bootstrap-vue/dist/bootstrap-vue.esm.min'
import { COLOR_PALETTE, GRANULARITY_LEVELS, GRANULARITY_LABELS, renderDashboardChartTooltip } from './dashboard-helpers'

export default {
  name: 'UserCallsChart',
  components: {
    BDropdown,
    BDropdownItem
  },
  props: {
    // { labels: [...], extensions: { ext: name }, calls: { ext: { label: count } } }
    chartData: { type: Object, default: () => ({ labels: [], extensions: {}, calls: {} }) },
    granularity: { type: String, default: 'hora' }
  },
  data () {
    return {
      chartType: 'line',
      levels: GRANULARITY_LEVELS
    }
  },
  computed: {
    canUp () {
      return this.levels.indexOf(this.granularity) > 0
    },
    canDown () {
      return this.levels.indexOf(this.granularity) < this.levels.length - 1
    }
  },
  watch: {
    chartData: {
      deep: true,
      handler () {
        this.renderChart()
      }
    }
  },
  mounted () {
    this.chart = null
    this.renderChart()
  },
  beforeDestroy () {
    if (this.chart) this.chart.destroy()
  },
  methods: {
    granularityLabel (level) {
      return GRANULARITY_LABELS[level] || level
    },
    step (direction) {
      const idx = this.levels.indexOf(this.granularity)
      const next = direction === 'up' ? idx - 1 : idx + 1
      if (next < 0 || next >= this.levels.length) return
      this.$emit('set-granularity', this.levels[next])
    },
    buildDatasets () {
      const extensions = (this.chartData && this.chartData.extensions) || {}
      const calls = (this.chartData && this.chartData.calls) || {}
      const datasets = []
      let colorIndex = 0
      for (const ext in calls) {
        datasets.push({
          label: extensions[ext] ? extensions[ext] + ' (' + ext + ')' : ext,
          data: Object.values(calls[ext]),
          backgroundColor: COLOR_PALETTE[colorIndex % COLOR_PALETTE.length],
          borderColor: COLOR_PALETTE[colorIndex % COLOR_PALETTE.length],
          pointBorderWidth: 0,
          pointRadius: 2,
          borderWidth: 1,
          fill: false
        })
        colorIndex++
      }
      return datasets
    },
    setChartType (type) {
      if (this.chartType === type) return
      this.chartType = type
      this.renderChart(true)
    },
    // recreate=true força destruir e recriar (troca de tipo line/bar).
    renderChart (recreate) {
      const labels = (this.chartData && this.chartData.labels) || []
      const datasets = this.buildDatasets()
      if (this.chart && !recreate) {
        this.chart.data.labels = labels
        this.chart.data.datasets = datasets
        this.chart.update()
        return
      }
      if (this.chart) {
        this.chart.destroy()
        this.chart = null
      }
      this.chart = new Chart(this.$refs.canvas, {
        type: this.chartType,
        data: {
          labels: labels,
          datasets: datasets
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          title: { display: false },
          legend: {
            display: true,
            position: 'bottom',
            labels: {
              usePointStyle: true,
              boxWidth: 6,
              padding: 16,
              fontSize: 11,
              fontColor: '#64748b',
              fontFamily: "'Inter', ui-sans-serif, system-ui, sans-serif"
            }
          },
          tooltips: {
            enabled: false,
            mode: 'index',
            intersect: false,
            custom: renderDashboardChartTooltip
          },
          hover: {
            mode: 'index',
            intersect: false
          },
          scales: {
            xAxes: [{
              gridLines: {
                display: true,
                drawBorder: true,
                color: '#e2e8f0',
                zeroLineColor: '#e2e8f0',
                lineWidth: 1,
                borderDash: [4, 4],
                drawTicks: true,
                tickMarkLength: 5
              }
            }],
            yAxes: [{
              ticks: {
                min: 0
              },
              stacked: false,
              gridLines: {
                display: true,
                drawBorder: true,
                color: '#e2e8f0',
                zeroLineColor: '#e2e8f0',
                lineWidth: 1,
                borderDash: [4, 4],
                drawTicks: true,
                tickMarkLength: 5
              }
            }]
          }
        }
      })
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/Dashboard/UserCallsChart.vue